import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import * as api from '../api/endpoints';
import { Button, Card, ErrorBanner, TextField } from '../components/ui';
import { answerList, selectionOf, useAssessmentStore } from '../state/assessmentStore';

const EMPTY = { email: '', firstName: '', lastName: '', title: '', company: '' };

export function GetReport() {
  const navigate = useNavigate();
  const focus = useAssessmentStore((s) => s.focus);
  const pillars = useAssessmentStore((s) => s.pillars);
  const answers = useAssessmentStore((s) => s.answers);
  const setReport = useAssessmentStore((s) => s.setReport);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string>();

  const submit = useMutation({
    mutationFn: () =>
      api.submitReport({
        focus: focus ?? '',
        contact: form,
        selection: selectionOf(pillars),
        answers: answerList(answers),
      }),
    onSuccess: ({ summary }) => {
      setReport({ contact: form, summary });
      navigate('/report');
    },
    onError: (err: Error) => setError(err.message),
  });

  // No WAR chosen -> nothing to submit; send them back to the chooser.
  if (!focus) return <Navigate to="/" replace />;

  const update = (key: keyof typeof form) => (value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-6">
      <div className="max-w-lg">
        <h1 className="text-2xl font-medium text-climb-paper">Get your report</h1>
        <p className="mt-2 text-climb-teal-soft">
          Enter your details to see your maturity report and high-risk gaps. We’ll email you a copy.
        </p>
      </div>

      <Card className="max-w-lg">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(undefined);
            submit.mutate();
          }}
        >
          {error ? <ErrorBanner message={error} /> : null}
          <div className="grid grid-cols-2 gap-4">
            <TextField
              id="firstName"
              label="First name"
              autoComplete="given-name"
              required
              value={form.firstName}
              onChange={(e) => update('firstName')(e.target.value)}
            />
            <TextField
              id="lastName"
              label="Last name"
              autoComplete="family-name"
              required
              value={form.lastName}
              onChange={(e) => update('lastName')(e.target.value)}
            />
          </div>
          <TextField
            id="email"
            label="Work email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => update('email')(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <TextField
              id="title"
              label="Title"
              autoComplete="organization-title"
              required
              value={form.title}
              onChange={(e) => update('title')(e.target.value)}
            />
            <TextField
              id="company"
              label="Company"
              autoComplete="organization"
              required
              value={form.company}
              onChange={(e) => update('company')(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={submit.isPending} className="w-full">
            {submit.isPending ? 'Preparing your report…' : 'Show my report'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
